import { sceneGLBLookupKey } from '@context/utils/linked/buildLinkedCatalogId'
import { applyIdCambioFromApi } from '@context/utils/linked/linkedUnitVariants'
import { invalidateLinkedTextureCaches } from '@context/utils/linked/invalidateLinkedTextureCaches'
import {
  applyLinkedUnitOptionsPatch,
  mapLinkedUnitOptionsResponse,
} from '@utils/sceneObjects/linked/fetchLinkedUnitOptions'
import { rebuildLinkedItemZonesFromUnit } from '@utils/sceneObjects/linked/rebuildLinkedItemZonesFromUnit'
import type { LinkedVariantPrefetchBundle } from '@utils/sceneObjects/linked/prefetchLinkedVariantCatalogs'
import type { LinkedItem, Store } from '@types'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { adjustChildrenPositionsAfterInheritance } from '@utils/sceneObjects/items/adjustChildrenToEncimera'

export type ApplyLinkedGlbVariantOptions = {
  linkedOptionsByIdUnidades?: Map<number, Record<string, unknown>>
  skipSonsUpdate?: boolean
  updateLinkedSonsForNewGLB?: (
    itemId: string,
    newCatalogId: string,
    previousCatalogId?: string,
  ) => Promise<unknown>
}

function normalizeCatalogCode(catalogId: string): string {
  return sceneGLBLookupKey(catalogId)
}

function isVitrinaCatalog(codeKey: string): boolean {
  const upper = codeKey.toUpperCase()
  return (
    upper.startsWith('KABV') ||
    upper.startsWith('KAPV') ||
    upper.startsWith('PE KABV') ||
    upper.startsWith('PE KAPV')
  )
}

function stripAcabadoSuffix(catalogID: string): string {
  return catalogID.replace(/ (GO|LA GO|RU GO|R2 GO| LI| LA| RU| R2)/, '')
}

/**
 * Aplica un cambio de variante GLB a un linkedItem usando datos precargados (sin API ni loadAsync).
 */
export function applyLinkedItemGlbVariantInStore(
  store: Store,
  itemId: string,
  newCatalogId: string,
  bundle: LinkedVariantPrefetchBundle,
  previousCatalogId: string,
  options?: ApplyLinkedGlbVariantOptions,
): boolean {
  const item = store.items.get(itemId)
  if (!item || !isLinkedItem(item)) return false

  const codeKey = bundle.codeKey
  const unidadData = bundle.unidadData
  const glb = bundle.glb

  if (!store.sceneGLB.has(codeKey)) {
    store.sceneGLB.set(codeKey, { glb, backGLBInfo: unidadData })
  }

  item.catalogID = codeKey

  const ref = unidadData.referenciaBlender
  if (typeof ref === 'string' && ref) {
    item.glbPath = ref
  }
  if (typeof unidadData.referencia === 'string') {
    item.referencia = unidadData.referencia
  }
  if (typeof unidadData.referenciaJpgpng === 'string') {
    item.image = unidadData.referenciaJpgpng
  }
  if (unidadData.idUnidades != null) {
    item.idUnidades = unidadData.idUnidades as number
    invalidateLinkedTextureCaches()
  }
  if (typeof unidadData.descripcion === 'string') {
    item.description = unidadData.descripcion as typeof item.description
  }
  if (typeof unidadData.descripcionLarga === 'string') {
    item.long = unidadData.descripcionLarga
  }
  if (typeof unidadData.ancho === 'number') {
    item.width = unidadData.ancho
  }
  if (typeof unidadData.fondo === 'number') {
    item.thickness = unidadData.fondo
  }
  if (typeof unidadData.alto === 'number') {
    item.height = unidadData.alto
  }

  applyIdCambioFromApi(item as Record<string, unknown>, unidadData)
  item.golaActiva = codeKey.includes(' GO')

  const revision = ((item as LinkedItem & { glbRevision?: number }).glbRevision ?? 0) + 1
  ;(item as LinkedItem & { glbRevision?: number }).glbRevision = revision
  item.glbSize = { x: revision, y: 1, z: 1 } as typeof item.glbSize

  const userAppliedTexturesByZoneName = new Map<string, unknown>()
  item.textures?.forEach((texture) => {
    if (texture.zoneName) {
      userAppliedTexturesByZoneName.set(texture.zoneName, texture)
    }
  })

  rebuildLinkedItemZonesFromUnit({
    item,
    newUnidadData: unidadData,
    glbSceneChildren: glb.scene?.children,
    acabadosZonasExtra: bundle.acabadosZonasExtra,
    userAppliedTexturesByZoneName: userAppliedTexturesByZoneName as Map<string, never>,
    isVitrina: isVitrinaCatalog(codeKey),
    itemId,
    source: 'applyLinkedItemGlbVariant',
  })

  const idUnidades = item.idUnidades
  if (idUnidades && options?.linkedOptionsByIdUnidades?.has(idUnidades)) {
    const payload = options.linkedOptionsByIdUnidades.get(idUnidades)!
    const patch = mapLinkedUnitOptionsResponse(payload)
    applyLinkedUnitOptionsPatch(item, patch, { preserveExisting: true })
  }

  const normalizedNewCatalogID = normalizeCatalogCode(newCatalogId)
  const baseCatalogID = stripAcabadoSuffix(previousCatalogId)
  const baseNewCatalogID = stripAcabadoSuffix(normalizedNewCatalogID)

  if (
    !options?.skipSonsUpdate &&
    previousCatalogId !== normalizedNewCatalogID &&
    baseCatalogID !== baseNewCatalogID &&
    options?.updateLinkedSonsForNewGLB
  ) {
    // Llamada async fuera del set — el batch la orquesta después
    return true
  }

  if (previousCatalogId !== normalizedNewCatalogID && baseCatalogID === baseNewCatalogID) {
    item.childs?.forEach((childID) => {
      const child = store.items.get(childID)
      if (!child || child.type !== 'linkedSon' || child.description === 'Tirador') return
      const x = child.x ?? 0
      const y = child.y ?? 0
      const z = child.z ?? 0
      if (!(x === 0 && y === 0 && z === 0)) {
        child.visible = true
      }
    })
  }

  return true
}

export async function finalizeLinkedItemGlbVariantSideEffects(
  get: () => Store,
  editItem: (id: string, updates: Record<string, unknown>) => void,
  itemId: string,
  newCatalogId: string,
  previousCatalogId: string,
  options?: ApplyLinkedGlbVariantOptions,
): Promise<void> {
  const normalizedNewCatalogID = normalizeCatalogCode(newCatalogId)
  const baseCatalogID = stripAcabadoSuffix(previousCatalogId)
  const baseNewCatalogID = stripAcabadoSuffix(normalizedNewCatalogID)

  if (
    previousCatalogId !== normalizedNewCatalogID &&
    baseCatalogID !== baseNewCatalogID &&
    options?.updateLinkedSonsForNewGLB
  ) {
    await options.updateLinkedSonsForNewGLB(itemId, newCatalogId, previousCatalogId)
  }

  const updatedItem = get().items.get(itemId)
  if (
    updatedItem &&
    isLinkedItem(updatedItem) &&
    updatedItem.childs &&
    Array.isArray(updatedItem.childs) &&
    updatedItem.childs.length > 0
  ) {
    adjustChildrenPositionsAfterInheritance(
      updatedItem,
      (childId) => get().items.get(childId),
      (childId, updates) => {
        editItem(childId, updates)
      },
    )
  }
}
